import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/lib/auth-context";
import { AppLayout } from "@/components/layout/AppLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  BookOpen, FileText, Search, ArrowLeft, ChevronRight, HelpCircle,
  Shield, GraduationCap, UserCheck, FolderOpen, Settings, Monitor,
  Sparkles, ClipboardList, Video, Users, Layers, ChevronDown, ChevronUp,
  Clock, Eye, Tag, ArrowRight, Zap, BookMarked,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ICON_MAP = { BookOpen, FileText, Shield, GraduationCap, UserCheck, FolderOpen, Settings, Monitor, Sparkles, ClipboardList, Video, Users, HelpCircle, Layers, Zap, BookMarked };

const renderMd = (md) => {
  if (!md) return "";
  // Handle fenced code blocks first
  let html = md.replace(/```(\w*)\n([\s\S]*?)```/g, (_, lang, code) => {
    const escaped = code.replace(/</g, '&lt;').replace(/>/g, '&gt;').trim();
    return `<div class="my-4 rounded-xl overflow-hidden border border-border/50"><div class="flex items-center justify-between px-4 py-2 text-[11px] font-mono text-muted-foreground bg-muted/80 border-b border-border/30"><span>${lang || 'shell'}</span></div><pre class="p-4 overflow-x-auto text-[13px] leading-relaxed bg-[#0d1117] text-[#e6edf3]"><code>${escaped}</code></pre></div>`;
  });
  return html
    .replace(/^---$/gm, '<hr class="my-6 border-border/50" />')
    .replace(/^### (.+)$/gm, '<h3 class="text-base font-semibold mt-5 mb-2 text-foreground flex items-center gap-2"><span class="w-1.5 h-1.5 rounded-full bg-primary inline-block"></span>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 class="text-lg font-bold mt-7 mb-3 pb-2 border-b border-border/50 text-foreground">$1</h2>')
    .replace(/^# (.+)$/gm, '<h1 class="text-xl font-bold mt-5 mb-4 text-foreground">$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-foreground">$1</strong>')
    .replace(/`(.+?)`/g, '<code class="px-1.5 py-0.5 rounded-md bg-primary/10 text-primary text-[13px] font-mono">$1</code>')
    .replace(/^> (.+)$/gm, '<div class="border-l-3 border-primary/40 pl-4 py-2 my-3 bg-primary/5 rounded-r-lg text-sm text-muted-foreground">$1</div>')
    .replace(/^(\d+)\. (.+)$/gm, '<div class="flex gap-3 my-1.5 items-start"><span class="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center mt-0.5">$1</span><span class="text-sm leading-relaxed">$2</span></div>')
    .replace(/^- (.+)$/gm, '<div class="flex gap-2 my-1 items-start ml-1"><span class="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 mt-2 flex-shrink-0"></span><span class="text-sm leading-relaxed">$1</span></div>')
    .replace(/\n\n/g, '<div class="h-2"></div>');
};

export default function DocumentationPage() {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [allArticles, setAllArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedCat, setSelectedCat] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState({});
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [c, a] = await Promise.all([
          fetch(`${API_URL}/api/documentation/categories`, { headers }).then(r => r.json()),
          fetch(`${API_URL}/api/documentation/articles`, { headers }).then(r => r.json()),
        ]);
        setCategories(c); setAllArticles(a);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  const GetIcon = ({ name, ...p }) => { const I = ICON_MAP[name] || FileText; return <I {...p} />; };

  const filtered = useMemo(() => {
    if (!search) return [];
    const q = search.toLowerCase();
    return allArticles.filter(a => a.title?.toLowerCase().includes(q) || a.content?.toLowerCase().includes(q) || (a.tags||[]).some(t=>t.toLowerCase().includes(q)));
  }, [search, allArticles]);

  const faqAll = allArticles.filter(a => a.is_faq);

  // ── Article View ──
  if (selectedArticle) {
    const cat = categories.find(c => c.id === selectedArticle.category_id);
    const siblings = allArticles.filter(a => a.category_id === selectedArticle.category_id && !a.is_faq);
    const curIdx = siblings.findIndex(a => a.id === selectedArticle.id);
    const next = siblings[curIdx + 1];
    const prev = siblings[curIdx - 1];

    return (
      <AppLayout>
        <div className="page-container animate-fade-in">
          <div className="max-w-5xl mx-auto flex gap-8">
            {/* Sidebar TOC */}
            <aside className="hidden lg:block w-56 flex-shrink-0 sticky top-24 self-start">
              <button onClick={() => { setSelectedArticle(null); }} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary mb-4 transition-colors">
                <ArrowLeft className="h-3 w-3" /> Retour
              </button>
              <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground/60 mb-2">Dans cette catégorie</p>
              <div className="space-y-0.5">
                {siblings.map(s => (
                  <button key={s.id} onClick={() => setSelectedArticle(s)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-all ${s.id === selectedArticle.id ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}`}>
                    {s.title}
                  </button>
                ))}
              </div>
            </aside>

            {/* Article Content */}
            <div className="flex-1 min-w-0">
              <button onClick={() => setSelectedArticle(null)} className="lg:hidden flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-4">
                <ArrowLeft className="h-4 w-4" /> Retour
              </button>

              {/* Breadcrumb */}
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4">
                <button onClick={() => { setSelectedArticle(null); setSelectedCat(null); }} className="hover:text-primary">Documentation</button>
                <ChevronRight className="h-3 w-3" />
                {cat && <button onClick={() => { setSelectedArticle(null); setSelectedCat(cat); }} className="hover:text-primary">{cat.title}</button>}
                <ChevronRight className="h-3 w-3" />
                <span className="text-foreground font-medium">{selectedArticle.title}</span>
              </div>

              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm" style={{ background: cat ? `${cat.color}12` : 'var(--muted)' }}>
                  <GetIcon name={selectedArticle.icon} className="h-6 w-6" style={{ color: cat?.color || 'var(--primary)' }} />
                </div>
                <div>
                  <h1 className="text-2xl font-bold leading-tight">{selectedArticle.title}</h1>
                  {selectedArticle.summary && <p className="text-muted-foreground mt-1">{selectedArticle.summary}</p>}
                </div>
              </div>

              {selectedArticle.tags?.length > 0 && (
                <div className="flex gap-1.5 mb-6 flex-wrap">
                  {selectedArticle.tags.map(t => (
                    <span key={t} className="text-[11px] px-2.5 py-1 rounded-full bg-muted text-muted-foreground font-medium">#{t}</span>
                  ))}
                </div>
              )}

              <Card className="shadow-sm border-border/50">
                <CardContent className="p-6 md:p-8">
                  <div dangerouslySetInnerHTML={{ __html: renderMd(selectedArticle.content) }} />
                </CardContent>
              </Card>

              {/* Prev/Next Nav */}
              <div className="flex justify-between mt-6 gap-4">
                {prev ? (
                  <button onClick={() => setSelectedArticle(prev)} className="flex items-center gap-2 px-4 py-3 rounded-xl border hover:border-primary/30 hover:bg-primary/5 transition-all text-left flex-1">
                    <ArrowLeft className="h-4 w-4 text-muted-foreground" />
                    <div><p className="text-[10px] text-muted-foreground">Précédent</p><p className="text-sm font-medium">{prev.title}</p></div>
                  </button>
                ) : <div />}
                {next ? (
                  <button onClick={() => setSelectedArticle(next)} className="flex items-center gap-2 px-4 py-3 rounded-xl border hover:border-primary/30 hover:bg-primary/5 transition-all text-right flex-1 justify-end">
                    <div><p className="text-[10px] text-muted-foreground">Suivant</p><p className="text-sm font-medium">{next.title}</p></div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </button>
                ) : <div />}
              </div>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  // ── Category View ──
  if (selectedCat) {
    const catArts = allArticles.filter(a => a.category_id === selectedCat.id && !a.is_faq);
    const catFaq = allArticles.filter(a => a.category_id === selectedCat.id && a.is_faq);

    return (
      <AppLayout>
        <div className="page-container max-w-4xl mx-auto animate-fade-in">
          <button onClick={() => setSelectedCat(null)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-6">
            <ArrowLeft className="h-4 w-4" /> Documentation
          </button>

          {/* Category Header */}
          <div className="rounded-2xl p-6 md:p-8 mb-8 relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${selectedCat.color}15, ${selectedCat.color}05)`, border: `1px solid ${selectedCat.color}20` }}>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg" style={{ background: `linear-gradient(135deg, ${selectedCat.color}, ${selectedCat.color}cc)` }}>
                <GetIcon name={selectedCat.icon} className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">{selectedCat.title}</h1>
                <p className="text-muted-foreground">{selectedCat.description} · {catArts.length} article{catArts.length !== 1 ? 's' : ''}</p>
              </div>
            </div>
          </div>

          {/* Articles */}
          <div className="space-y-3">
            {catArts.map((a, i) => (
              <Card key={a.id} className="group hover:shadow-lg transition-all cursor-pointer border-border/50 hover:border-primary/20 overflow-hidden" onClick={() => setSelectedArticle(a)}>
                <CardContent className="flex items-center gap-4 py-4 px-5">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110" style={{ background: `${selectedCat.color}10` }}>
                    <GetIcon name={a.icon} className="h-5 w-5" style={{ color: selectedCat.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold group-hover:text-primary transition-colors">{a.title}</p>
                    <p className="text-sm text-muted-foreground truncate mt-0.5">{a.summary}</p>
                  </div>
                  <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-xs text-primary font-medium">Lire</span>
                    <ArrowRight className="h-4 w-4 text-primary" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Category FAQ */}
          {catFaq.length > 0 && (
            <div className="mt-10">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                <HelpCircle className="h-5 w-5 text-amber-500" /> Questions fréquentes
              </h2>
              <div className="space-y-2">{catFaq.map(q => (
                <div key={q.id} className="rounded-xl border border-border/50 overflow-hidden hover:border-amber-200 transition-colors">
                  <button className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-amber-50/50 dark:hover:bg-amber-900/10 transition-colors"
                    onClick={() => setExpandedFaq(p => ({ ...p, [q.id]: !p[q.id] }))}>
                    <div className="w-7 h-7 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                      <HelpCircle className="h-3.5 w-3.5 text-amber-500" />
                    </div>
                    <span className="font-medium text-sm flex-1">{q.faq_question || q.title}</span>
                    {expandedFaq[q.id] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                  {expandedFaq[q.id] && <div className="px-5 pb-4 border-t border-border/30"><div className="pt-3 text-sm text-muted-foreground" dangerouslySetInnerHTML={{ __html: renderMd(q.content) }} /></div>}
                </div>
              ))}</div>
            </div>
          )}
        </div>
      </AppLayout>
    );
  }

  // ── Home ──
  return (
    <AppLayout>
      <div className="page-container animate-fade-in">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-2xl mb-10" style={{ background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 30%, #4338ca 60%, #6366f1 100%)" }}>
          <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(circle at 30% 40%, rgba(255,255,255,0.06) 0%, transparent 50%), radial-gradient(circle at 70% 60%, rgba(255,255,255,0.04) 0%, transparent 50%)" }} />
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-10" style={{ background: "radial-gradient(circle, #818cf8, transparent 70%)", filter: "blur(60px)" }} />
          <div className="relative p-8 md:p-12">
            <div className="flex items-center gap-2 mb-3">
              <div className="px-3 py-1 rounded-full text-[11px] font-semibold tracking-wider uppercase" style={{ background: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.8)" }}>
                📚 Centre d'aide
              </div>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Documentation DevOpsLearn</h1>
            <p className="text-indigo-200 text-base md:text-lg max-w-xl">Guides, tutoriels et réponses à vos questions pour maîtriser la plateforme</p>

            <div className="relative mt-8 max-w-2xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-indigo-300" />
              <input type="text" placeholder="Rechercher un article, un sujet..." value={search} onChange={e => setSearch(e.target.value)}
                className="w-full pl-12 pr-5 py-4 rounded-xl text-sm text-white placeholder:text-indigo-300/60 focus:outline-none focus:ring-2 focus:ring-white/20 transition-all"
                style={{ background: "rgba(255,255,255,0.1)", backdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.15)" }} />
            </div>
          </div>
        </div>

        {/* Search Results */}
        {search && (
          <div className="mb-10">
            <p className="text-sm text-muted-foreground mb-4">{filtered.length} résultat{filtered.length !== 1 ? 's' : ''} pour <strong>"{search}"</strong></p>
            {filtered.length === 0 ? (
              <Card className="border-dashed"><CardContent className="py-12 text-center text-muted-foreground"><Search className="h-8 w-8 mx-auto mb-3 opacity-20" /><p>Aucun résultat trouvé</p></CardContent></Card>
            ) : (
              <div className="space-y-2">{filtered.map(a => {
                const cat = categories.find(c => c.id === a.category_id);
                return (
                  <Card key={a.id} className="hover:shadow-md transition-all cursor-pointer group" onClick={() => { setSelectedArticle(a); setSearch(""); }}>
                    <CardContent className="flex items-center gap-4 py-3 px-5">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: cat ? `${cat.color}10` : 'var(--muted)' }}>
                        <GetIcon name={a.icon} className="h-4 w-4" style={{ color: cat?.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm group-hover:text-primary">{a.title}</p>
                        <p className="text-xs text-muted-foreground truncate">{cat?.title} · {a.summary}</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100" />
                    </CardContent>
                  </Card>
                );
              })}</div>
            )}
          </div>
        )}

        {!search && (
          <>
            {/* Categories Grid */}
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3 mb-12">
              {loading ? [1,2,3].map(i => <div key={i} className="h-52 skeleton rounded-2xl" />) :
              categories.map(cat => {
                const count = allArticles.filter(a => a.category_id === cat.id && !a.is_faq).length;
                return (
                  <Card key={cat.id} className="group cursor-pointer overflow-hidden border-0 shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-1" onClick={() => setSelectedCat(cat)}>
                    <div className="h-1.5 transition-all duration-300" style={{ background: `linear-gradient(90deg, ${cat.color}, ${cat.color}88)` }} />
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3" style={{ background: `linear-gradient(135deg, ${cat.color}20, ${cat.color}08)`, border: `1px solid ${cat.color}25` }}>
                          <GetIcon name={cat.icon} className="h-7 w-7" style={{ color: cat.color }} />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-lg group-hover:text-primary transition-colors">{cat.title}</h3>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{cat.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between pt-3 border-t border-border/30">
                        <span className="text-xs text-muted-foreground">{count} article{count !== 1 ? 's' : ''}</span>
                        <span className="flex items-center gap-1 text-xs font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                          Explorer <ArrowRight className="h-3 w-3" />
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* FAQ */}
            {faqAll.length > 0 && (
              <div className="max-w-3xl mx-auto">
                <div className="text-center mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-amber-500/10 flex items-center justify-center mx-auto mb-3">
                    <HelpCircle className="h-6 w-6 text-amber-500" />
                  </div>
                  <h2 className="text-2xl font-bold">Questions fréquentes</h2>
                  <p className="text-muted-foreground text-sm mt-1">Retrouvez les réponses aux questions les plus posées</p>
                </div>
                <div className="space-y-2">
                  {faqAll.map(q => (
                    <div key={q.id} className="rounded-xl border border-border/50 overflow-hidden hover:border-amber-200/50 transition-colors">
                      <button className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-amber-50/30 dark:hover:bg-amber-900/5 transition-colors"
                        onClick={() => setExpandedFaq(p => ({ ...p, [q.id]: !p[q.id] }))}>
                        <div className="w-7 h-7 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                          <HelpCircle className="h-3.5 w-3.5 text-amber-500" />
                        </div>
                        <span className="font-medium text-sm flex-1">{q.faq_question || q.title}</span>
                        <div className={`transition-transform duration-200 ${expandedFaq[q.id] ? 'rotate-180' : ''}`}>
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </button>
                      <div className="overflow-hidden transition-all duration-300" style={{ maxHeight: expandedFaq[q.id] ? '500px' : '0', opacity: expandedFaq[q.id] ? 1 : 0 }}>
                        <div className="px-5 pb-4 border-t border-border/30">
                          <div className="pt-3 text-sm text-muted-foreground" dangerouslySetInnerHTML={{ __html: renderMd(q.content) }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
